import { useState, useEffect, useCallback } from "react";

// カスタムイベントを発火させるためのヘルパー関数（同一タブ内での同期用）
const dispatchStorageEvent = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("local-storage-update"));
  }
};

export function useLocalStorage(key, initialValue) {
  // useCallbackでラップし、不要な再生成を防ぐ
  const readValue = useCallback(() => {
    if (typeof window === "undefined") {
      return initialValue;
    }
    try {
      const item = window.localStorage.getItem(key);
      // itemが "undefined" という文字列で保存されているケースへの対策
      if (item === null || item === "undefined") return initialValue;
      return JSON.parse(item);
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  }, [key, initialValue]);

  const [storedValue, setStoredValue] = useState(readValue);

  const setValue = (value) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      if (typeof window !== "undefined") {
        window.localStorage.setItem(key, JSON.stringify(valueToStore));
        dispatchStorageEvent(); // 更新を他のコンポーネントに通知
      }
    } catch (error) {
      console.warn(`Error setting localStorage key "${key}":`, error);
    }
  };

  // 初回マウント時、または他のタブ/コンポーネントからの変更を検知して同期する
  useEffect(() => {
    setStoredValue(readValue());

    const handleStorageChange = (e) => {
      // e.keyがない場合は全クリアなどのイベント、もしくは対象のキーが一致した場合
      if (!e.key || e.key === key) {
        setStoredValue(readValue());
      }
    };

    const handleLocalChange = () => {
      setStoredValue(readValue());
    };

    // 別タブからの変更を検知
    window.addEventListener("storage", handleStorageChange);
    // 同一タブ内の他コンポーネントからの変更を検知
    window.addEventListener("local-storage-update", handleLocalChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("local-storage-update", handleLocalChange);
    };
  }, [key, readValue]);

  return [storedValue, setValue];
}

// Reactフックとして正しく機能するように修正
export function useLocalStorageValue(key) {
  const [value, setValue] = useState(() => {
    if (typeof window === "undefined") return null;
    try {
      const item = window.localStorage.getItem(key);
      if (item === null || item === "undefined") return null;
      return JSON.parse(item);
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error);
      return null;
    }
  });

  useEffect(() => {
    const updateValue = () => {
      try {
        const item = window.localStorage.getItem(key);
        setValue(item && item !== "undefined" ? JSON.parse(item) : null);
      } catch (error) {
        setValue(null);
      }
    };

    const handleStorageChange = (e) => {
      if (!e.key || e.key === key) {
        updateValue();
      }
    };

    // 別タブからの変更を検知
    window.addEventListener("storage", handleStorageChange);
    // 同一タブ内の他コンポーネントからの変更を検知
    window.addEventListener("local-storage-update", updateValue);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("local-storage-update", updateValue);
    };
  }, [key]);

  return value;
}

export function removeLocalStorage(keys) {
  try {
    keys.forEach((key) => {
      window.localStorage.removeItem(key);
    });
    dispatchStorageEvent(); // 削除時も他のコンポーネントに通知
  } catch (error) {
    console.error("Failed to remove localStorage:", error);
  }
}
